import { useState, useEffect } from 'react';
import {
  Button,
  Collapse,
  Form,
  Row,
  Col,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Alert,
} from 'react-bootstrap';

import cx from 'classnames';
import { addMinutes, format } from 'date-fns';

import CompareIcon from '../../../CompareIcon';

import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';

import {
  CompareStatus,
  qaCompareSimpleValue,
  qaContainsInText,
} from 'pages/QA/components/QACardDetails/utils';

import Style from '../../index.module.css';
import { LeverandorKundenummerDto } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import { ChangeRequestValidator } from 'pages/QA/components/QACardDetails/ChangeRequestValidator';

interface Props {
  value?: Date | null;
  enhet: {
    oppstartsdato?: Date;
    lopenr?: string;
    leverandorKundenummer?: LeverandorKundenummerDto[];
  };
  onChange: (date: Date | null | undefined) => void;
  changeRequest: {
    nedlagtDato?: Date;
    leverandorKundenummerHosTsKundeId: string;
    leverandorKundenummerHos: string;
    leverandorKundenummer: string;
  };
}

const NedlagtDatoRow = (props: Props) => {
  const [isExpandRow, setIsExpandRow] = useState<boolean>(false);
  const [compareStatus, setCompareStatus] = useState<CompareStatus>(
    qaContainsInText(
      props.changeRequest.nedlagtDato
        ? format(props.changeRequest.nedlagtDato, 'dd.MM.yyyy')
        : null,
      props.value ? format(props.value, 'dd.MM.yyyy') : null,
    ),
  );
  const [crValueError, setCrValueError] = useState<string | null>(null);

  const [showError, setShowError] = useState<boolean>(false);

  const handleDateChanged = (date: Date | null | undefined) => {
    // Handle null/undefined case when clearing the date field
    if (!date) {
      setShowError(false);
      props.onChange(null);
      return;
    }

    if (props.enhet?.oppstartsdato && date <= props.enhet.oppstartsdato) {
      setShowError(true);
      props.onChange(null);
      return;
    } else {
      setShowError(false);
      // handle timezone issue
      const adjustedDate = addMinutes(date, -date.getTimezoneOffset());
      props.onChange(adjustedDate);
    }
  };

  const calculateMinDate = (date: Date | undefined, addDays: number) =>
    date ? new Date(date.getTime() + addDays * 86400000) : undefined;

  useEffect(() => {
    // Only auto-expand on differences, but don't auto-collapse when values match
    if (compareStatus === 'ulik' || compareStatus === 'ny') {
      setIsExpandRow(true);
    }
  }, [compareStatus]);

  useEffect(() => {
    setCompareStatus(
      qaCompareSimpleValue(formatDate(props.changeRequest.nedlagtDato), formatDate(props.value)),
    );
  }, [props.changeRequest.nedlagtDato, props.value]);

  const formatDate = (date: Date | undefined): string => {
    return date ? format(date, 'dd.MM.yyyy') : '';
  };

  const hasCrValue =
    props.changeRequest.nedlagtDato &&
    props.changeRequest.nedlagtDato.getDate() !== props.value?.getDate();

  // Check if the value from the change request has any errors
  useEffect(() => {
    if (props.changeRequest.nedlagtDato) {
      const error = ChangeRequestValidator.hasNedlagtDatoErrors(props.changeRequest.nedlagtDato);
      setCrValueError(error);
    } else {
      setCrValueError(null);
    }
  }, [props.changeRequest.nedlagtDato]);

  return (
    <>
      <div
        className={cx(Style.changeRequestRowHeaderContainer, {
          [Style.isNotExpandedRowContainer]: !isExpandRow,
        })}
        onClick={() => setIsExpandRow((prev) => !prev)}
      >
        <h6 className={`${Style.changeRequestRowHeader} w-100`}>
          <Row className="g-0 w-100 align-items-center">
            <Col xs={5} className="d-flex align-items-center gap-1">
              {!isExpandRow && <CompareIcon compareStatus={compareStatus} size="sm" />}
              <span
                className={cx(Style.changeRequestRowHeaderText, {
                  [Style.changeRequestRowHeaderTextRed]: hasCrValue,
                })}
              >
                Nedlagtdato
                {!isExpandRow && props.changeRequest.nedlagtDato && (
                  <span className="ms-1 fw-normal">
                    {formatDate(props.changeRequest.nedlagtDato)}
                  </span>
                )}
              </span>
            </Col>
            <Col xs={2} />
            <Col xs={4} className="d-flex align-items-center">
              {!isExpandRow && props.value && (
                <span className={Style.changeRequestRowHeaderText + ' fw-normal'}>
                  {formatDate(props.value)}
                </span>
              )}
            </Col>
            <Col xs={1} className="d-flex justify-content-end">
              <Button variant="link" size="sm">
                <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
              </Button>
            </Col>
          </Row>
        </h6>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              {hasCrValue ? (
                <InputGroup size="sm">
                  <Form.Control
                    className={Style.formControl}
                    readOnly
                    value={formatDate(props.changeRequest.nedlagtDato)}
                    isValid={!crValueError && !!props.changeRequest.nedlagtDato}
                    isInvalid={!!crValueError}
                  />
                  {props.changeRequest.nedlagtDato && (
                    <InputGroup.Text>
                      <CopyToClipboardIcon val={formatDate(props.changeRequest.nedlagtDato)} />
                    </InputGroup.Text>
                  )}
                  <Form.Control.Feedback type="invalid">
                    {crValueError ?? 'Nedlagtdato er ugyldig.'}
                  </Form.Control.Feedback>
                </InputGroup>
              ) : (
                <Form.Control
                  className={Style.formControl}
                  readOnly
                  value={formatDate(props.changeRequest.nedlagtDato)}
                  isValid={!crValueError && !!props.changeRequest.nedlagtDato}
                  isInvalid={!!crValueError}
                />
              )}
            </Col>
            {showError ? (
              <Col xs="auto" md={2} className={Style.compareIconContainer}>
                <OverlayTrigger
                  key="tooltip-top"
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip">Nedlagt dato må være høyere enn oppstartdato</Tooltip>
                  }
                >
                  <div className={Style.changeRequestCompareIcon}>
                    <IcebergIcon icon="close" backgroundColor="grey" color="#fff" bold rounded />
                  </div>
                </OverlayTrigger>
              </Col>
            ) : (
              <Col xs="auto" md={2} className={Style.compareIconContainer}>
                {!!crValueError ? (
                  <CompareIcon compareStatus="ugyldig" tooltipText={crValueError} />
                ) : (
                  <CompareIcon
                    compareStatus={compareStatus}
                    onClick={() => handleDateChanged(props.changeRequest.nedlagtDato)}
                  />
                )}
              </Col>
            )}
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <NorwegianDatepicker
                  showLastDaySuggestions
                  placeholder="dd.mm.yyyy"
                  value={props.value}
                  onChange={(d) => handleDateChanged(d)}
                  minDate={calculateMinDate(props.enhet?.oppstartsdato, 1)}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      </Collapse>
    </>
  );
};

export default NedlagtDatoRow;
